import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useMutation } from '@apollo/client';
import styles from '../styles.module.scss';
import { useContactPicker } from '../ContactPickerContext';
import { useFieldHistory } from '../FieldHistoryContext';
import {
  UPDATE_DEAL_FIELD_GROUP,
  BIND_CONTACT_TO_FIELD_GROUP,
  DELETE_DEAL_FIELD_GROUP,
} from '../../../../api/placeholders/mutations';
import { queriesV2 } from '../../../../api';
import { ReactComponent as TrashIcon } from '../../../../shared/icons/trashcan-icon.svg';
import { ReactComponent as EditIcon } from '../../../../shared/icons/edit-icon.svg';
import { ReactComponent as CloseIcon } from '../../../../shared/icons/close-icon.svg';
import { ReactComponent as HistoryIcon } from '../../../../shared/icons/history-icon.svg';

/** Matches the debounce in `PlaceholderItem`, so both value inputs feel the same. */
const VALUE_DEBOUNCE_MS = 500;

interface DealFieldItemProps {
  fieldGroup: any;
  contacts: any[];
  documentId: string;
  dealId: string;
  teamId: string;
  isReadOnly?: boolean;
  canLinkContacts?: boolean;
  onShowUpgradeModal?: () => void;
  onUpdate: () => void;
}

const DealFieldItem: React.FC<DealFieldItemProps> = ({
  fieldGroup,
  contacts: _contacts,
  documentId,
  dealId: _dealId,
  teamId,
  isReadOnly = false,
  canLinkContacts = true,
  onShowUpgradeModal,
  onUpdate,
}) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [localValue, setLocalValue] = useState(fieldGroup.value || '');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingValueRef = useRef<string | null>(null);
  const { openContactPicker, openEditContact } = useContactPicker();
  const { openHistory } = useFieldHistory();

  const hasContactBinding = fieldGroup.contactBinding?.contact_id;

  /**
   * Push a deal-field value into the DOCUMENT for every placeholder linked to
   * this field. The "This Deal" tab is the only place this can happen from —
   * `DocumentFieldsList` (whose items do their own syncing) is unmounted while
   * this tab is showing, so nothing else would reach the body.
   */
  const syncLinkedPlaceholdersToEditor = useCallback(
    (newValue: string, linkedGroupId: string | null) => {
      for (const link of fieldGroup.placeholderLinks || []) {
        const uuid = link.placeholder?.uuid;
        const placeholderName = link.placeholder?.title;
        if (!uuid && !placeholderName) continue;
        window.dispatchEvent(
          new CustomEvent('updatePlaceholderValue', {
            detail: { placeholderId: uuid, placeholderName, newValue, linkedGroupId },
          })
        );
      }
    },
    [fieldGroup.placeholderLinks]
  );

  // Refetch queries for placeholder completeness validation after mutations
  const completenessRefetchQueries = [
    {
      query: queriesV2.getDocumentPlaceholderCompleteness,
      variables: { documentId: String(documentId), documentType: 'Document' },
    },
  ];

  // GraphQL mutations - all include refetchQueries to keep completeness validation in sync
  const [updateFieldGroup] = useMutation(UPDATE_DEAL_FIELD_GROUP, { refetchQueries: completenessRefetchQueries });
  const [bindContact] = useMutation(BIND_CONTACT_TO_FIELD_GROUP, { refetchQueries: completenessRefetchQueries });
  const [deleteFieldGroup, { loading: isDeleting }] = useMutation(DELETE_DEAL_FIELD_GROUP, { refetchQueries: completenessRefetchQueries });

  /**
   * Persist a deal-field value.
   *
   * A deal field fans out: the mutation propagates the value to every linked
   * placeholder in every document of the deal, and each of those is then written
   * into the open editor's body. Sending that per keystroke made typing a name a
   * few dozen fan-outs; it is debounced to one, like the sibling
   * `PlaceholderItem`. `flushValue` exists so leaving the field commits
   * immediately rather than waiting out the timer.
   */
  const commitValue = useCallback(
    async (newValue: string) => {
      try {
        await updateFieldGroup({
          variables: {
            id: fieldGroup.id,
            value: newValue,
          },
        });
        syncLinkedPlaceholdersToEditor(newValue, fieldGroup.id);
        onUpdate();
      } catch (error) {
        // Silent failure - user can retry
      }
    },
    [fieldGroup.id, updateFieldGroup, syncLinkedPlaceholdersToEditor, onUpdate]
  );

  const handleValueChange = useCallback(
    (newValue: string) => {
      setLocalValue(newValue);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      pendingValueRef.current = newValue;
      debounceRef.current = setTimeout(() => {
        debounceRef.current = null;
        pendingValueRef.current = null;
        void commitValue(newValue);
      }, VALUE_DEBOUNCE_MS);
    },
    [commitValue]
  );

  /** Commit a pending edit now — on blur, so the value is never left in flight. */
  const flushValue = useCallback(() => {
    if (!debounceRef.current) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = null;
    const pending = pendingValueRef.current;
    pendingValueRef.current = null;
    if (pending != null) void commitValue(pending);
  }, [commitValue]);

  // An unmount mid-edit (tab switch, sidebar close) must not drop the keystrokes
  // the timer was still holding.
  useEffect(() => {
    return () => {
      if (!debounceRef.current) return;
      clearTimeout(debounceRef.current);
      const pending = pendingValueRef.current;
      if (pending != null) void commitValue(pending);
    };
  }, [commitValue]);

  const handleBindContact = useCallback(
    async (contactId: string | null, field: string | null) => {
      try {
        // Unbind the contact
        await bindContact({
          variables: {
            dealFieldGroupId: fieldGroup.id,
            contactId,
            contactField: field,
          },
        });

        // If unlinking (contactId is null), also clear the value
        if (contactId === null) {
          await updateFieldGroup({
            variables: {
              id: fieldGroup.id,
              value: '',
            },
          });
          setLocalValue('');

          // Every linked placeholder falls back to its empty-state label.
          syncLinkedPlaceholdersToEditor('', null);
        }

        onUpdate();
      } catch (error) {
        // Silent failure - user can retry
      }
    },
    [fieldGroup.id, bindContact, updateFieldGroup, syncLinkedPlaceholdersToEditor, onUpdate]
  );

  const handleOpenContactPicker = () => {
    if (!canLinkContacts) {
      onShowUpgradeModal?.();
      return;
    }
    openContactPicker({
      teamId,
      placeholderName: fieldGroup.name,
      onSelect: handleBindContact,
    });
  };

  const handleEditContact = () => {
    const contact = fieldGroup.boundContact;
    if (contact) {
      openEditContact(contact);
    }
  };

  const handleDelete = useCallback(async () => {
    try {
      const result = await deleteFieldGroup({
        variables: { id: fieldGroup.id },
      });

      // Check for GraphQL response errors (Apollo doesn't throw for these)
      const responseData = result.data?.deleteDealFieldGroup;
      if (responseData?.errors?.length > 0) {
        console.error('Delete failed:', responseData.errors);
        return; // Don't close modal - let user retry
      }

      if (!responseData?.success) {
        console.error('Delete failed: success was false');
        return; // Don't close modal - let user retry
      }

      setShowDeleteModal(false);
      onUpdate();
    } catch (error) {
      // Network error - modal stays open so user can retry
      console.error('Delete failed with exception:', error);
    }
  }, [fieldGroup.id, deleteFieldGroup, onUpdate]);

  return (
    <>
      <div className={styles.dealFieldItem}>
        <div className={styles.dealFieldHeader}>
          <span className={styles.itemLabel}>{fieldGroup.name}</span>
          <div className={styles.dealFieldHeaderRight}>
            <span className={styles.dealFieldDocCount}>
              {fieldGroup.documentCount} doc{fieldGroup.documentCount !== 1 ? 's' : ''}
            </span>
            <button
              className={styles.iconButton}
              onClick={() =>
                openHistory({ scope: 'dealField', title: fieldGroup.name, dealFieldGroupId: fieldGroup.id })
              }
              title="Value history"
            >
              <HistoryIcon className={styles.iconButtonSvg} />
            </button>
            {!isReadOnly && (
              <button
                className={styles.trashButton}
                onClick={() => setShowDeleteModal(true)}
                title="Delete field"
              >
                <TrashIcon className={styles.trashIcon} />
              </button>
            )}
          </div>
        </div>

        {hasContactBinding ? (
          <div className={styles.contactCard}>
            <div className={styles.contactCardValue}>
              <div>{fieldGroup.boundContact?.name}</div>
              <div>→ {fieldGroup.contactBinding.field}</div>
            </div>
            {!isReadOnly && (
              <div className={styles.contactCardActions}>
                <button
                  className={styles.iconButton}
                  onClick={handleEditContact}
                  title="Edit contact"
                >
                  <EditIcon className={styles.iconButtonSvg} />
                </button>
                <button
                  className={styles.iconButton}
                  onClick={() => handleBindContact(null, null)}
                  title="Unlink contact"
                >
                  <CloseIcon className={styles.iconButtonSvg} />
                </button>
              </div>
            )}
          </div>
        ) : (
          <>
            <input
              type="text"
              className={styles.valueInput}
              value={localValue}
              onChange={(e) => handleValueChange(e.target.value)}
              onBlur={flushValue}
              placeholder="Enter value..."
              disabled={isReadOnly}
            />

            {!isReadOnly && (
              <button className={styles.bindButton} onClick={handleOpenContactPicker}>
                👤 Select Contact
              </button>
            )}
          </>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className={styles.deleteModal} onClick={() => setShowDeleteModal(false)}>
          <div className={styles.deleteModalContent} onClick={(e) => e.stopPropagation()}>
            <h3 className={styles.deleteModalTitle}>Delete Deal Field</h3>
            <p className={styles.deleteModalText}>
              Are you sure you want to delete "{fieldGroup.name}"?
              {fieldGroup.documentCount > 0 && (
                <>
                  {' '}This will unlink {fieldGroup.documentCount} placeholder{fieldGroup.documentCount !== 1 ? 's' : ''} across your documents.
                </>
              )}
            </p>
            <div className={styles.deleteModalActions}>
              <button
                className={styles.cancelButton}
                onClick={() => setShowDeleteModal(false)}
              >
                Cancel
              </button>
              <button
                className={styles.deleteButton}
                onClick={handleDelete}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DealFieldItem;
